import { useContext, useEffect, useLayoutEffect, useState } from "react"
import { AvailabilityByPhoneInput, DateInput, DropdownMenu, EditBtn, SafeEditBtn, SaveCancelBox, TextInput } from "./Common"
import "./ProfileData.css"
import { LocaleContext, LoginContext, OverlayContext, ScrollContext, StateContext } from "./App"
import { get_translation_objects, postWithCsrf, checkEmail } from "./util";
import { AvailabilityByPhone, Gender, Graduation, LoadState, Location, Notifications, Recommendation, Salutation, Title, UserType } from "./enums";
import { Documents } from "./Documents";
import { maxCityLength, maxCurrentEmploymentLength, maxNameLength, maxPhoneNoLength, maxPostCodeLength, maxStreetAndHouseNoLength, maxUsernameLength } from "./globals_common";
import { validateCity, validateCurrentEmployment, validateName, validatePhoneNumber, validatePostCode, validateStreetAndHouseNo } from "./validate";
import { useSafeEdit } from "./hooks";

export const ProfileData = ({userDataOrig, setUserDataOrig, onUserDataChange, readOnly, userId, userType, cancelTrg,
                             showDocuments, email, setEmail, tabBasepath, lockNavigation}) => {
    const [editable, setEditable] = useState(false);
    const [userData, setUserData] = useState(userDataOrig);
    const [oldUserData, setOldUserData] = useState(null);
    const [oldEmail, setOldEmail] = useState(null);
    const [saveState, setSaveState] = useState(LoadState.NONE);

    const [emailState, setEmailState] = useState(email);

    if(onUserDataChange === undefined) {
        onUserDataChange = (value) => {};
    }

    const locale = useContext(LocaleContext);
    const scroll = useContext(ScrollContext);
    const state = useContext(StateContext);
    const overlay = useContext(OverlayContext);


    // FUNCTIONS:

    function isSavable() {
        if(!editable) {
            return false;
        }

        if(JSON.stringify(userData) === JSON.stringify(oldUserData) && oldEmail === emailState) {
            return false;
        }

        return true;
    }

    function setEditableFunc(e) {
        safeEdit.setDisplayEditTooltip(false);
        setEditable(e);
    }

    function handleEdit() {
        if(editable) {
            return;
        }
        
        setOldUserData(JSON.parse(JSON.stringify(userData)));
        setOldEmail(emailState);
        setEditableFunc(true);
        setSaveState(LoadState.NONE);
    }

    function handleSave() {
        try {
            const data = {
                location: userData.location,
                salutation: userData.salutation,
                title: userData.title,
                first_name: userData.first_name,
                last_name: userData.last_name,

                birth_date: userData.birth_date,
                gender: userData.gender,
                phone_number: userData.phone_number,
                graduation: userData.graduation,
                current_employment: userData.current_employment,

                street_and_house_no: userData.street_and_house_no,
                post_code: userData.post_code,
                city: userData.city,
                availability_by_phone: userData.availability_by_phone,
                specific_availability_by_phone: userData.specific_availability_by_phone,
                recommendation: userData.recommendation
            };

            postWithCsrf("/user/management/set_user_data_data", JSON.stringify({"user_id": userId, "care_data_data": data, "email": emailState }), state).then(response => {
                if(response.ok) {
                    return response.json();
                }
                else {

                    if(response.status === 520) {
                        overlay.setErrorPopupInfo({"title": locale.msgs.GEOCODING_ERROR, "text": locale.msgs.GEOCODING_ERROR_DESCR});
                        overlay.setErrorPopupDisplayed(true);
                    }

                    throw new Error("Response code: " + response.status);
                }
            }).then(json => {
                setSaveState(LoadState.LOAD_SUCCESS);
                setEditableFunc(false);

                setUserData(json["care_data"]);
                setUserDataOrig(json["care_data"]);
                setEmailState(json["email"]);
                setEmail(json["email"]);
            }).catch(error => {
                console.log(error);
                setSaveState(LoadState.LOAD_ERROR);
            });

            // Calling setSaveState() after postWithCsrf() as it could throw an exception
            setSaveState(LoadState.LOADING);
        }
        catch(e) {
            console.log(e);
        }
    }

    function handleCancel() {
        setEditableFunc(false);
        setUserData(oldUserData);
        setEmailState(oldEmail);
        setSaveState(LoadState.NONE);
    }

    function setUserDataAndResetSaveState(userData) {
        setUserData(userData);
        setSaveState(LoadState.NONE);
    }


    // EFFECTS:

    useEffect(() => {
        if(editable) {
            handleCancel();
        }
    }, [cancelTrg]);

    useEffect(() => {
        if(!editable) {
            setUserData(userDataOrig);
        }

        setOldUserData(userDataOrig);
    }, [userDataOrig]);

    useEffect(() => {
        setEmailState(email);
    }, [email]);

    const safeEdit = useSafeEdit(editable, userData, oldUserData, lockNavigation);


    // RENDER:

    const [genderTransList, genderTransListInverse] = get_translation_objects("gender", Gender, locale);
    const [salutationTransList, salutationTransListInverse] = get_translation_objects("salutation", Salutation, locale);
    const [titleTransList, titleTransListInverse] = get_translation_objects("title", Title, locale);
    const [locationTransList, locationTransListInverse] = get_translation_objects("location", Location, locale);
    const [graduationTransList, graduationTransListInverse] = get_translation_objects("graduation", Graduation, locale);
    const [notificationsTransList, notificationsTransListInverse] = get_translation_objects("notifications", Notifications, locale);
    const [recTransList, recTransListInverse] = get_translation_objects("recommendation", Recommendation, locale);

    return (
        <div className="profile_data_box tab_switch_paading">
            { !readOnly ?
                <SafeEditBtn safeEdit={safeEdit} editable={editable} onClick={handleEdit} />
            :
                null
            }

            <div ref={safeEdit.editBoxRef} className="profile_data_content input_minimal_gap">
                <DropdownMenu minimal={true} readOnly={!editable} text={locale.msgs.MN_LOCATION} options={Object.values(locationTransList)}
                            value={locationTransList[userData["location"]]}
                            onValueChange={(value) => setUserDataAndResetSaveState({...userData, "location": locationTransListInverse[value]})}
                            onScroll={scroll.scrollTo} />

                <div className="profile_data_hori_box">
                    <DropdownMenu minimal={true} readOnly={!editable} text={locale.msgs.SALUTATION} options={Object.values(salutationTransList)}
                            value={salutationTransList[userData["salutation"]]}
                            onValueChange={(value) => setUserDataAndResetSaveState({...userData, "salutation": salutationTransListInverse[value]})}
                            onScroll={scroll.scrollTo} />

                    <DropdownMenu minimal={true} readOnly={!editable} text={locale.msgs.TITLE} options={Object.values(titleTransList)}
                            value={titleTransList[userData["title"]]}
                            onValueChange={(value) => setUserDataAndResetSaveState({...userData, "title": titleTransListInverse[value]})}
                            optional={true} noText="-"
                            onScroll={scroll.scrollTo} />
                </div>

                <div className="profile_data_hori_box">
                    <TextInput minimal={true} readOnly={!editable} text={locale.msgs.FIRST_NAME} value={userData["first_name"]}
                            onChange={(value) => setUserDataAndResetSaveState({...userData, "first_name": value})}
                            errorText={locale.msgs.PLEASE_ENTER_VALID_NAME}
                            checker={(value) => validateName(value)}
                            maxLength={maxNameLength} />

                    <TextInput minimal={true} readOnly={!editable} text={locale.msgs.LAST_NAME} value={userData["last_name"]}
                            onChange={(value) => setUserDataAndResetSaveState({...userData, "last_name": value})}
                            errorText={locale.msgs.PLEASE_ENTER_VALID_NAME}
                            checker={(value) => validateName(value)}
                            maxLength={maxNameLength} />
                </div>

                {
                    userType === UserType.FAMILY ? null :

                        <div className="profile_data_hori_box">
                            <DateInput minimal={true} readOnly={!editable} text={locale.msgs.BIRTHDAY} value={userData["birth_date"]}
                                    onChange={(value) => setUserDataAndResetSaveState({...userData, "birth_date": value})}
                                    legalAge={true} />

                            <DropdownMenu minimal={true} readOnly={!editable} text={locale.msgs.GENDER} options={Object.values(genderTransList)}
                                    value={genderTransList[userData["gender"]]}
                                    onValueChange={(value) => setUserDataAndResetSaveState({...userData, "gender": genderTransListInverse[value]})}
                                    onScroll={scroll.scrollTo} />
                        </div>
                }

                <TextInput minimal={true} readOnly={!editable} text={locale.msgs.EMAIL_ADDRESS} value={emailState}
                        onChange={(value) => setEmailState(value) }
                        type="email"
                        checker={(v) => checkEmail(v)}
                        errorText={locale.msgs.ENTER_VALID_EMAIL_ADDRESS}
                        maxLength={maxUsernameLength} />

                <TextInput minimal={true} readOnly={!editable} text={locale.msgs.TELEPHONE_NO} value={userData["phone_number"]}
                        onChange={(value) => setUserDataAndResetSaveState({...userData, "phone_number": value})}
                        errorText={locale.msgs.ENTER_VALID_PHONE_NO}
                        checker={(value) => validatePhoneNumber(value)}
                        maxLength={maxPhoneNoLength} />

                {
                    userType === UserType.FAMILY ? null :

                    <DropdownMenu  minimal={true} readOnly={!editable} text={locale.msgs.GRADUATION} options={Object.values(graduationTransList)}
                            value={graduationTransList[userData["graduation"]]}
                            onValueChange={(value) => setUserDataAndResetSaveState({...userData, "graduation": graduationTransListInverse[value]})}
                            onScroll={scroll.scrollTo} />
                }

                {
                    userType === UserType.FAMILY ? null :

                    <TextInput minimal={true} readOnly={!editable} text={locale.msgs.CURRENT_EMPLOYMENT} value={userData["current_employment"]}
                            onChange={(value) => setUserDataAndResetSaveState({...userData, "current_employment": value})}
                            errorText={locale.msgs.INVALID_VALUE}
                            checker={(value) => validateCurrentEmployment(value)}
                            maxLength={maxCurrentEmploymentLength} />
                }

                { true ?
                
                    <TextInput minimal={true} readOnly={!editable} text={locale.msgs.STREET_AND_HOUSE_NO} value={userData["street_and_house_no"]}
                            onChange={(value) => setUserDataAndResetSaveState({...userData, "street_and_house_no": value})}
                            valToReturnOnError="" // Return an empty string as the backend won't accept null
                            errorText={locale.msgs.PLEASE_ENTER_VALID_STREET_AND_HOUSE_NO}
                            checker={(value) => validateStreetAndHouseNo(value)}
                            maxLength={maxStreetAndHouseNoLength} />
                :
                    null
                }

                <div className="profile_data_hori_box">
                    <TextInput minimal={true} readOnly={!editable} text={locale.msgs.POST_CODE} value={userData["post_code"]}
                            onChange={(value) => setUserDataAndResetSaveState({...userData, "post_code": value})}
                            errorText={locale.msgs.PLEASE_ENTER_VALID_POST_CODE}
                            checker={(value) => validatePostCode(value)}
                            maxLength={maxPostCodeLength} />

                    <TextInput minimal={true} readOnly={!editable} text={locale.msgs.CITY} value={userData["city"]}
                            onChange={(value) => setUserDataAndResetSaveState({...userData, "city": value})}
                            errorText={locale.msgs.PLEASE_ENTER_VALID_CITY}
                            checker={(value) => validateCity(value)}
                            maxLength={maxCityLength}/>
                </div>

                {/*
                <DropdownMenu minimal={true} readOnly={!editable} text={locale.msgs.NOTIFICATIONS} options={Object.values(notificationsTransList)}
                        value={notificationsTransList[userData["notifications"]]}
                        onValueChange={(value) => setUserDataAndResetSaveState({...userData, "notifications": notificationsTransListInverse[value]})}
                        onScroll={scroll.scrollTo} />
                */}

                <AvailabilityByPhoneInput editable={editable} careData={userData} onCareDataChange={(careData) => setUserDataAndResetSaveState(careData)}
                        onScroll={scroll.scrollTo} />

                <DropdownMenu minimal={true} readOnly={!editable} text={locale.msgs.HOW_FIND_US} options={Object.values(recTransList)}
                        value={recTransList[userData["recommendation"]]}
                        onValueChange={(value) => setUserDataAndResetSaveState({...userData, "recommendation": recTransListInverse[value]})}
                        onScroll={scroll.scrollTo} />

                { showDocuments && userType === UserType.NANNY ?
                    <div className="profile_data_files_box">
                        <div className="text_big_3">{userType === UserType.NANNY ? locale.msgs.FILES : locale.msgs.DESIRED_LANGUAGES}</div>
                        <Documents management={true} userId={userId} readOnly={true} hideEmpty={true} tabBasepath={tabBasepath} smallLoading={true}
                                lockNavigation={false} />
                    </div>
                :
                    null
                }
                

                { !readOnly ?
                    <SaveCancelBox editable={editable} savable={isSavable()} saving={saveState === LoadState.LOADING} saved={saveState === LoadState.LOAD_SUCCESS}
                            notSaved={saveState === LoadState.LOAD_ERROR} onSaveClick={handleSave} onCancelClick={handleCancel}
                            lockNavigation={lockNavigation}
                    />
                :
                    null
                }
            </div>
        </div>
    )
}
